import React, { ReactElement, useEffect } from "react";
import { useState } from "react";
import { Button } from "react-aria-components";

interface IWizard {
    children: React.ReactNode,
    initialData?: {} | undefined,
    initialStep?: number
}

interface StepComponentProps {
    nextStep: () => void;
    prevStep: () => void;
    handleChange: (input: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    values: Record<string, any>;
    errors: Record<string, string>;
}

interface StepComponent extends React.FC<StepComponentProps> {
    validate?: (values: Record<string, any>) => Record<string, string>;
}

export type Change = {
    value: string | number | boolean | null;
    elementName?: string;
};

export default function Wizard({ children, initialData, initialStep = 0 }: IWizard) {
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [formValues, setFormValues] = useState<any>(initialData ?? {});
    const [errors, setErrors] = useState({});

    const nextStep = () => {
        if (validateStep()) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleChange = (input: string) => (value: string | number | boolean) => {
        const updatedValues = { ...formValues, [input]: value };
        setFormValues(updatedValues);
        runValidationWithValues(updatedValues);
    };

    const handleChangeBatched = (changes: Change[]) => {
        const updatedValues = { ...formValues };
        changes.forEach(({ value, elementName }) => {
            updatedValues[elementName!] = value;
        });
        setFormValues(updatedValues);
        runValidationWithValues(updatedValues);
    };

    const cancel = () => {
        window.history.back();
    }

    useEffect(() => {
        validateStep()
    }, [formValues, currentStep])

    const runValidationWithValues = (values: Record<string, any>) => {
        const activeStep = React.Children.toArray(children)[currentStep] as ReactElement<any> & { type: StepComponent };
        if (activeStep.type.validate) {
            const validationErrors = activeStep.type.validate(values);
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return false;
            }
        }
        setErrors({});
        return true;
    };

    const validateStep = () => {
        return runValidationWithValues(formValues);
    };

    const childArray = React.Children.toArray(children) as ReactElement[]
    const currentChild = childArray[currentStep] as ReactElement;
    const childProps = {
        nextStep,
        prevStep,
        handleChange,
        handleChangeBatched,
        values: formValues,
        errors,
        cancel
    };

    return (
        <>
            <div className="wizard__tabs-wrapper">
                {childArray.map((x, i) =>
                    <Button data-currentTab={i === currentStep} className="wizard__tab" onPress={() => {}}>
                        <h3 className="wizard__tab--step-colour">Step {i + 1}</h3>
                        <h3 className="wizard__tab--name-colour">{x.props.displayName}</h3>
                    </Button>)}
            </div>
            {React.cloneElement(currentChild, childProps)}
        </>
    );
}